import { createFFmpeg, fetchFile } from '@ffmpeg/ffmpeg';

const DEFAULT_CORE_URL =
    'https://unpkg.com/@ffmpeg/core-st@0.11.1/dist/ffmpeg-core.js';
const debugVideo = (stage, details) => {
    globalThis.console.info('[creative-video]', stage, details ?? '');
};

let ffmpegPromise;
let ffmpegCommandQueue = Promise.resolve();

const runFfmpegQueued = (task) => {
    const run = ffmpegCommandQueue.then(task, task);
    ffmpegCommandQueue = run.catch(() => undefined);
    return run;
};

const isIgnorableCommandError = (error) =>
    error?.name === 'ExitStatus' && error?.status === 0;

const isConcurrentCommandError = (error) =>
    typeof error?.message === 'string' &&
    error.message.includes('can only run one command at a time');

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const MIN_COMMAND_TIMEOUT_MS = 60 * 1000;
const MAX_COMMAND_TIMEOUT_MS = 10 * 60 * 1000;
const COMMAND_TIMEOUT_MS_PER_SECOND = 15 * 1000;

// Wall-time budget for one ffmpeg command, scaled by the video length. A
// wasm abort or OOM never emits FFMPEG_END, so without it the queue hangs.
export const getFfmpegCommandTimeoutMs = (durationSeconds) => {
    const seconds = Number(durationSeconds);
    if (!Number.isFinite(seconds) || seconds <= 0) {
        return MAX_COMMAND_TIMEOUT_MS;
    }
    return Math.min(
        MAX_COMMAND_TIMEOUT_MS,
        Math.max(
            MIN_COMMAND_TIMEOUT_MS,
            seconds * COMMAND_TIMEOUT_MS_PER_SECOND
        )
    );
};

export class FfmpegTimeoutError extends Error {
    constructor(operation, timeoutMs) {
        super(`Video ${operation} timed out after ${timeoutMs}ms`);
        this.name = 'FfmpegTimeoutError';
    }
}

const gcd = (a, b) => {
    let x = Math.abs(a);
    let y = Math.abs(b);
    while (y !== 0) {
        const t = y;
        y = x % y;
        x = t;
    }
    return x || 1;
};

const toFraction = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric <= 0) {
        throw new Error('Invalid video trim parameters');
    }

    let text = String(value).trim();
    if (!text || text.includes('e') || text.includes('E')) {
        text = numeric.toFixed(6);
    }
    text = text.replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1');

    const [integerPartRaw, decimalPartRaw = ''] = text.split('.');
    const integerPart = Number(integerPartRaw || 0);
    const decimalPart = decimalPartRaw.replace(/[^0-9]/g, '');

    if (!decimalPart) {
        return { numerator: integerPart, denominator: 1 };
    }

    const denominator = 10 ** decimalPart.length;
    const numerator = integerPart * denominator + Number(decimalPart);
    const divisor = gcd(numerator, denominator);

    return {
        numerator: numerator / divisor,
        denominator: denominator / divisor,
    };
};

const recycleFfmpeg = (ffmpeg) => {
    try {
        ffmpeg.exit?.();
    } catch {}
    ffmpegPromise = undefined;
};

const runWithTimeout = (ffmpeg, args, operation, timeoutMs) => {
    let timer;
    const timeout = new Promise((_, reject) => {
        timer = setTimeout(() => {
            // exit() rejects the pending run and frees the wasm instance.
            recycleFfmpeg(ffmpeg);
            reject(new FfmpegTimeoutError(operation, timeoutMs));
        }, timeoutMs);
    });
    return Promise.race([ffmpeg.run(...args), timeout]).finally(() =>
        clearTimeout(timer)
    );
};

const getFFmpeg = () => {
    if (!ffmpegPromise) {
        const corePath =
            process.env.REACT_APP_FFMPEG_CORE_URL || DEFAULT_CORE_URL;
        debugVideo('ffmpeg.load.start', { corePath });
        const ffmpeg = createFFmpeg({
            corePath,
            mainName: 'main',
            logger: ({ type, message }) => {
                debugVideo(`ffmpeg.${type}`, message);
            },
        });
        ffmpegPromise = ffmpeg
            .load()
            .then(() => {
                debugVideo('ffmpeg.load.success');
                return ffmpeg;
            })
            .catch((error) => {
                debugVideo('ffmpeg.load.error', error);
                ffmpegPromise = undefined;
                throw error;
            });
    } else {
        debugVideo('ffmpeg.load.reuse');
    }

    return ffmpegPromise;
};

const unlink = (ffmpeg, path) => {
    try {
        ffmpeg.FS('unlink', path);
    } catch {}
};

const getVideoFormat = (file) => {
    const extension = file.name.toLowerCase().endsWith('.webm')
        ? 'webm'
        : 'mp4';
    return {
        extension,
        mimeType: extension === 'webm' ? 'video/webm' : 'video/mp4',
        codecArgs:
            extension === 'webm'
                ? ['-c:v', 'libvpx-vp9', '-c:a', 'libvorbis']
                : ['-c:v', 'libx264', '-preset', 'ultrafast', '-c:a', 'aac'],
    };
};

const runFfmpegCommand = async (ffmpeg, args, operation, timeoutMs) => {
    debugVideo(`${operation}.command.start`, args.join(' '));
    let commandError;
    const maxRunAttempts = 3;

    for (let attempt = 1; attempt <= maxRunAttempts; attempt++) {
        try {
            await runWithTimeout(ffmpeg, args, operation, timeoutMs);
            debugVideo(`${operation}.command.success`);
            commandError = undefined;
            break;
        } catch (error) {
            commandError = error;
            debugVideo(`${operation}.command.error`, error);
            if (
                !(error instanceof FfmpegTimeoutError) &&
                isConcurrentCommandError(error) &&
                attempt < maxRunAttempts
            ) {
                debugVideo(`${operation}.command.retry`, { attempt });
                await wait(75);
                continue;
            }
            break;
        }
    }

    if (commandError && !isIgnorableCommandError(commandError)) {
        throw commandError;
    }
    return commandError;
};

export const resizeVideoFile = async (
    file,
    targetWidth,
    targetHeight,
    { durationSeconds } = {}
) => {
    if (
        !file ||
        !Number.isInteger(targetWidth) ||
        targetWidth <= 0 ||
        !Number.isInteger(targetHeight) ||
        targetHeight <= 0
    ) {
        throw new Error('Invalid video resize parameters');
    }

    const { extension, mimeType, codecArgs } = getVideoFormat(file);
    const pixelFormat =
        extension === 'mp4'
            ? targetWidth % 2 !== 0 || targetHeight % 2 !== 0
                ? 'yuv444p'
                : 'yuv420p'
            : null;
    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const inputPath = `input-${id}.${extension}`;
    const outputPath = `output-${id}.${extension}`;

    return runFfmpegQueued(async () => {
        const ffmpeg = await getFFmpeg();
        try {
            ffmpeg.FS('writeFile', inputPath, await fetchFile(file));
            if (pixelFormat === 'yuv444p') {
                globalThis.console.warn(
                    '[creative-video] yuv444p has limited playback compatibility',
                    { targetWidth, targetHeight }
                );
            }
            const args = [
                '-i',
                inputPath,
                '-vf',
                // Without setsar=1, scale keeps the display aspect ratio through
                // a non-square SAR and players report e.g. 576x385 for 576x384.
                `scale=${targetWidth}:${targetHeight},setsar=1`,
                ...codecArgs,
                ...(pixelFormat ? ['-pix_fmt', pixelFormat] : []),
                outputPath,
            ];
            const commandError = await runFfmpegCommand(
                ffmpeg,
                args,
                'resize',
                getFfmpegCommandTimeoutMs(durationSeconds)
            );

            const output = ffmpeg.FS('readFile', outputPath);
            if (!output.length) {
                if (commandError) {
                    throw commandError;
                }
                throw new Error('Video resize produced an empty file');
            }
            if (commandError) {
                recycleFfmpeg(ffmpeg);
            }

            return new File(
                [output],
                file.name.replace(/\.[^.]+$/, '') + `.${extension}`,
                { type: mimeType, lastModified: Date.now() }
            );
        } catch (error) {
            // Never hand a possibly broken instance to the next item.
            recycleFfmpeg(ffmpeg);
            throw error;
        } finally {
            unlink(ffmpeg, inputPath);
            unlink(ffmpeg, outputPath);
        }
    });
};

export const trimVideoFile = async (file, duration) => {
    if (!file) {
        throw new Error('Invalid video trim parameters');
    }

    const { numerator: durationNumerator, denominator: durationDenominator } =
        toFraction(duration);
    const targetDuration = durationNumerator / durationDenominator;
    const frameRate = 30;
    const frameCount = Math.max(1, Math.round(targetDuration * frameRate));
    const fpsNumerator = frameCount * durationDenominator;
    const fpsDenominator = durationNumerator;
    const videoTimescale = frameCount * durationDenominator;

    const { extension, mimeType, codecArgs } = getVideoFormat(file);
    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const inputPath = `input-${id}.${extension}`;
    const outputPath = `output-${id}.${extension}`;
    debugVideo('trim.start', {
        name: file.name,
        type: file.type,
        bytes: file.size,
        targetDuration,
        frameRate,
        frameCount,
        fps: `${fpsNumerator}/${fpsDenominator}`,
    });

    return runFfmpegQueued(async () => {
        const ffmpeg = await getFFmpeg();
        try {
            const input = await fetchFile(file);
            debugVideo('trim.input.ready', { inputPath, bytes: input.length });
            ffmpeg.FS('writeFile', inputPath, input);
            const videoFilter = `fps=${fpsNumerator}/${fpsDenominator},trim=end_frame=${frameCount},setpts=PTS-STARTPTS`;

            const args = [
                '-i',
                inputPath,
                '-vf',
                videoFilter,
                '-vframes',
                String(frameCount),
                '-t',
                String(targetDuration),
                '-video_track_timescale',
                String(videoTimescale),
                ...codecArgs,
                outputPath,
            ];
            // The source is at most one second longer than the target.
            const commandError = await runFfmpegCommand(
                ffmpeg,
                args,
                'trim',
                getFfmpegCommandTimeoutMs(targetDuration + 1)
            );

            const output = ffmpeg.FS('readFile', outputPath);
            if (!output.length) {
                if (commandError) {
                    throw commandError;
                }
                throw new Error('Video trim produced an empty file');
            }
            if (commandError) {
                debugVideo('trim.command.recovered', {
                    name: commandError.name,
                    message: commandError.message,
                    status: commandError.status,
                });
                recycleFfmpeg(ffmpeg);
            }
            debugVideo('trim.output.ready', {
                outputPath,
                bytes: output.length,
            });

            return new File(
                [output],
                file.name.replace(/\.[^.]+$/, '') + `.${extension}`,
                {
                    type: mimeType,
                    lastModified: Date.now(),
                }
            );
        } catch (error) {
            debugVideo('trim.error', error);
            // Never hand a possibly broken instance to the next item.
            recycleFfmpeg(ffmpeg);
            throw error;
        } finally {
            unlink(ffmpeg, inputPath);
            unlink(ffmpeg, outputPath);
            debugVideo('trim.cleanup', { inputPath, outputPath });
        }
    });
};
